import { AppProps } from 'next/app';
import Script from 'next/script';
import { appWithTranslation } from 'next-i18next';

import { Provider } from 'react-redux';

import { Toaster } from '@nocowanie/common-ui/components/molecules/toaster';

// import { hindFont } from '@app/fonts/hind';
import { ThirdPartyScripts } from '@app/components/atoms/third-party-scripts';
import { AppHeader } from '@app/components/organisms/app-header';
import { AppHeaderOld } from '@app/components/organisms/app-header-old';
import { GrowthbookPagesProvider } from '@app/components/providers/growthbook-provider/growthbook-pages.provider';
import { environment } from '@app/environments/environment';
import { setMobile } from '@app/helpers/ssr.helpers';
import { useRemoveFragmentsOnMount } from '@app/hooks/useRemoveFragmentsOnMount';
import { PageProps } from '@app/models';
import nextI18nextConfig from '@app/next-i18next.config';
import { setMobileVersion } from '@app/state/slices/common-ui-state.slice';
import { nocowanieAppStateWrapper } from '@app/state/state';
import { ThemeProvider } from '@app/ui-providers';

import speculationRules from '../public/speculation-rules.json';

function NocowanieApp({ Component, ...rest }: AppProps<PageProps>) {
    const store = nocowanieAppStateWrapper.useStore();
    const { pageProps: props } = rest;

    useRemoveFragmentsOnMount();

    setMobile({
        userAgent: props.userAgent,
        store,
        action: setMobileVersion,
    });

    return (
        <>
            <ThirdPartyScripts
                gtm={{
                    tagId: environment.GTM.GTM_TAG_ID,
                    auth: environment.GTM.GTM_AUTH,
                    preview: environment.GTM.GTM_PREVIEW,
                }}
                wphPixelId={environment.WPH_PIXEL_ID}
            />
            <Provider store={store}>
                <ThemeProvider>
                    <GrowthbookPagesProvider>
                        {props.hideAppHeader ? null : props.appHeaderProps?.isHomePage ? (
                            <AppHeader {...props.appHeaderProps} />
                        ) : (
                            <AppHeaderOld {...props.appHeaderProps} />
                        )}
                        <Component {...props} />
                        <Toaster />
                    </GrowthbookPagesProvider>
                </ThemeProvider>
            </Provider>
            <Script
                id="speculation-rules"
                type="speculationrules"
                strategy="lazyOnload"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(speculationRules),
                }}
            />
        </>
    );
}

// We don't define `getInitialProps` due to https://github.com/vercel/next.js/discussions/47501#discussioncomment-5420368.
// If it will be needed, we will most propably have to 'duplicate' 500 page in `_error` page.

// https://github.com/i18next/next-i18next/issues/990
export default appWithTranslation(NocowanieApp, nextI18nextConfig);
